import { createSlice, createAction } from '@reduxjs/toolkit';
import { globalReset } from '@/store/actions';
import {
    SpeakingExercise,
    AdaptivePracticeRecommendation,
    ExerciseFilters,
    PaginatedResponse,
} from '@/services/speaking/types';
import { START_PAGE } from '@/common/constants';

// === SPEAKING ACTIONS ===
export const fetchExercisesRequest = createAction<ExerciseFilters | undefined>('speaking/fetchExercisesRequest');
export const fetchExercisesSuccess = createAction<PaginatedResponse<SpeakingExercise>>(
    'speaking/fetchExercisesSuccess',
);
export const fetchExercisesFailure = createAction<string>('speaking/fetchExercisesFailure');

export const fetchExerciseByIdRequest = createAction<string>('speaking/fetchExerciseByIdRequest');
export const fetchExerciseByIdSuccess = createAction<SpeakingExercise>('speaking/fetchExerciseByIdSuccess');
export const fetchExerciseByIdFailure = createAction<string>('speaking/fetchExerciseByIdFailure');

export const fetchPhonemeDetailsRequest = createAction<string>('speaking/fetchPhonemeDetailsRequest');
export const fetchPhonemeDetailsSuccess = createAction<any[]>('speaking/fetchPhonemeDetailsSuccess');
export const fetchPhonemeDetailsFailure = createAction<string>('speaking/fetchPhonemeDetailsFailure');

export const fetchRecommendedExercisesRequest = createAction('speaking/fetchRecommendedExercisesRequest');
export const fetchRecommendedExercisesSuccess = createAction<AdaptivePracticeRecommendation[]>(
    'speaking/fetchRecommendedExercisesSuccess',
);
export const fetchRecommendedExercisesFailure = createAction<string>('speaking/fetchRecommendedExercisesFailure');

export const rateRecommendationRequest = createAction<{ recommendationId: string; rating: number }>(
    'speaking/rateRecommendationRequest',
);
export const rateRecommendationSuccess = createAction<{ message: string }>('speaking/rateRecommendationSuccess');
export const rateRecommendationFailure = createAction<string>('speaking/rateRecommendationFailure');

// === PRONUNCIATION ALERTS ACTIONS ===
export const fetchMispronunciationAlertsRequest = createAction<number | undefined>(
    'speaking/fetchMispronunciationAlertsRequest',
);
export const fetchMispronunciationAlertsSuccess = createAction<{
    phonemes: Array<{
        phoneme: string;
        error_count: number;
        accuracy_rate: number;
        last_error: string;
        severity: 'minor' | 'moderate' | 'major';
    }>;
    should_notify: boolean;
    notification_message: string;
}>('speaking/fetchMispronunciationAlertsSuccess');
export const fetchMispronunciationAlertsFailure = createAction<string>('speaking/fetchMispronunciationAlertsFailure');

export const createPronunciationAlertRequest = createAction<{
    phoneme: string;
    error_type: string;
    severity: 'minor' | 'moderate' | 'major';
    message: string;
}>('speaking/createPronunciationAlertRequest');
export const createPronunciationAlertSuccess = createAction<{ notification_id: string; message: string }>(
    'speaking/createPronunciationAlertSuccess',
);
export const createPronunciationAlertFailure = createAction<string>('speaking/createPronunciationAlertFailure');

export const dismissPronunciationAlertRequest = createAction<string>('speaking/dismissPronunciationAlertRequest');
export const dismissPronunciationAlertSuccess = createAction<{ message: string }>(
    'speaking/dismissPronunciationAlertSuccess',
);
export const dismissPronunciationAlertFailure = createAction<string>('speaking/dismissPronunciationAlertFailure');

// === PHONEME PLAYBACK ACTIONS ===
export const fetchPhonemeTimingsRequest = createAction<string>('speaking/fetchPhonemeTimingsRequest');
export const fetchPhonemeTimingsSuccess = createAction<
    Array<{
        phoneme: string;
        start_time: number;
        end_time: number;
        accuracy: number;
        user_audio_segment: string;
        reference_audio_segment: string;
    }>
>('speaking/fetchPhonemeTimingsSuccess');
export const fetchPhonemeTimingsFailure = createAction<string>('speaking/fetchPhonemeTimingsFailure');

export const generatePhonemeAudioRequest = createAction<{ phoneme: string; voice: 'male' | 'female' }>(
    'speaking/generatePhonemeAudioRequest',
);
export const generatePhonemeAudioSuccess = createAction<{
    audio_url: string;
    phoneme: string;
    ipa_notation: string;
}>('speaking/generatePhonemeAudioSuccess');
export const generatePhonemeAudioFailure = createAction<string>('speaking/generatePhonemeAudioFailure');

// === WAVEFORM ANALYSIS ACTIONS ===
export const fetchWaveformDataRequest = createAction<string>('speaking/fetchWaveformDataRequest');
export const fetchWaveformDataSuccess = createAction<{
    user_waveform: Array<{ time: number; amplitude: number; frequency?: number }>;
    reference_waveform: Array<{ time: number; amplitude: number; frequency?: number }>;
    stress_markers: Array<{
        start_time: number;
        end_time: number;
        stress_type: 'primary' | 'secondary' | 'unstressed';
        intensity: number;
    }>;
    error_markers: Array<{
        start_time: number;
        end_time: number;
        error_type: string;
        severity: 'minor' | 'moderate' | 'major';
        phoneme: string;
    }>;
}>('speaking/fetchWaveformDataSuccess');
export const fetchWaveformDataFailure = createAction<string>('speaking/fetchWaveformDataFailure');

// === GPT FEEDBACK ACTIONS ===
export const fetchConversationalFeedbackRequest = createAction<{
    attemptId: string;
    tone: 'playful' | 'serious' | 'explanatory' | 'encouraging';
}>('speaking/fetchConversationalFeedbackRequest');
export const fetchConversationalFeedbackSuccess = createAction<{
    overall_feedback: string;
    phoneme_feedback: Array<{
        phoneme: string;
        feedback: string;
        suggestion: string;
        example_words: string[];
    }>;
    prosody_feedback: {
        stress_feedback: string;
        intonation_feedback: string;
        rhythm_feedback: string;
    };
    practice_recommendations: Array<{
        type: 'minimal_pairs' | 'shadowing' | 'repetition';
        description: string;
        exercises: string[];
    }>;
    encouragement: string;
    next_steps: string[];
}>('speaking/fetchConversationalFeedbackSuccess');
export const fetchConversationalFeedbackFailure = createAction<string>('speaking/fetchConversationalFeedbackFailure');

export const updateFeedbackToneRequest = createAction<{
    sessionId: string;
    tone: 'playful' | 'serious' | 'explanatory' | 'encouraging';
}>('speaking/updateFeedbackToneRequest');
export const updateFeedbackToneSuccess = createAction<{ message: string }>('speaking/updateFeedbackToneSuccess');
export const updateFeedbackToneFailure = createAction<string>('speaking/updateFeedbackToneFailure');

// === AUDIO COMPARISON ACTIONS ===
export const compareAudioSimilarityRequest = createAction<{ userAudioUrl: string; referenceAudioUrl: string }>(
    'speaking/compareAudioSimilarityRequest',
);
export const compareAudioSimilaritySuccess = createAction<{
    similarity_score: number;
    detailed_comparison: {
        pitch_similarity: number;
        rhythm_similarity: number;
        phoneme_accuracy: number;
        overall_quality: number;
    };
    recommendations: string[];
}>('speaking/compareAudioSimilaritySuccess');
export const compareAudioSimilarityFailure = createAction<string>('speaking/compareAudioSimilarityFailure');

// === AVATAR PRACTICE ACTIONS ===
export const fetchAvatarDemonstrationRequest = createAction<{ exerciseId: string; avatarType: '2d' | '3d' }>(
    'speaking/fetchAvatarDemonstrationRequest',
);
export const fetchAvatarDemonstrationSuccess = createAction<{
    demonstration_video_url: string;
    phoneme_timings: Array<{
        phoneme: string;
        start_time: number;
        end_time: number;
        mouth_shape: string;
        tongue_position: string;
    }>;
    synchronized_audio_url: string;
}>('speaking/fetchAvatarDemonstrationSuccess');
export const fetchAvatarDemonstrationFailure = createAction<string>('speaking/fetchAvatarDemonstrationFailure');

export const setExerciseFilters = createAction<ExerciseFilters>('speaking/setExerciseFilters');
export const clearExerciseFilters = createAction('speaking/clearExerciseFilters');
export const setCurrentFeedbackTone = createAction<'playful' | 'serious' | 'explanatory' | 'encouraging'>(
    'speaking/setCurrentFeedbackTone',
);

// === STATE INTERFACE ===
export interface SpeakingState {
    exercises: SpeakingExercise[];
    currentExercise: SpeakingExercise | null;
    phonemeDetails: any[];
    recommendedExercises: AdaptivePracticeRecommendation[];

    // Pronunciation Alerts
    mispronunciationAlerts: {
        phonemes: Array<{
            phoneme: string;
            error_count: number;
            accuracy_rate: number;
            last_error: string;
            severity: 'minor' | 'moderate' | 'major';
        }>;
        should_notify: boolean;
        notification_message: string;
    } | null;

    // Phoneme Playback
    phonemeTimings: Array<{
        phoneme: string;
        start_time: number;
        end_time: number;
        accuracy: number;
        user_audio_segment: string;
        reference_audio_segment: string;
    }>;
    generatedPhonemeAudio: {
        audio_url: string;
        phoneme: string;
        ipa_notation: string;
    } | null;

    // Waveform Data
    waveformData: {
        user_waveform: Array<{ time: number; amplitude: number; frequency?: number }>;
        reference_waveform: Array<{ time: number; amplitude: number; frequency?: number }>;
        stress_markers: Array<{
            start_time: number;
            end_time: number;
            stress_type: 'primary' | 'secondary' | 'unstressed';
            intensity: number;
        }>;
        error_markers: Array<{
            start_time: number;
            end_time: number;
            error_type: string;
            severity: 'minor' | 'moderate' | 'major';
            phoneme: string;
        }>;
    } | null;

    // GPT Feedback
    conversationalFeedback: {
        overall_feedback: string;
        phoneme_feedback: Array<{
            phoneme: string;
            feedback: string;
            suggestion: string;
            example_words: string[];
        }>;
        prosody_feedback: {
            stress_feedback: string;
            intonation_feedback: string;
            rhythm_feedback: string;
        };
        practice_recommendations: Array<{
            type: 'minimal_pairs' | 'shadowing' | 'repetition';
            description: string;
            exercises: string[];
        }>;
        encouragement: string;
        next_steps: string[];
    } | null;
    currentFeedbackTone: 'playful' | 'serious' | 'explanatory' | 'encouraging';

    // Audio Comparison
    audioComparison: {
        similarity_score: number;
        detailed_comparison: {
            pitch_similarity: number;
            rhythm_similarity: number;
            phoneme_accuracy: number;
            overall_quality: number;
        };
        recommendations: string[];
    } | null;

    // Avatar Practice
    avatarDemonstration: {
        demonstration_video_url: string;
        phoneme_timings: Array<{
            phoneme: string;
            start_time: number;
            end_time: number;
            mouth_shape: string;
            tongue_position: string;
        }>;
        synchronized_audio_url: string;
    } | null;

    // Pagination
    totalExercises: number;
    currentPage: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;

    // Filters
    filters: ExerciseFilters;

    // Loading States
    isLoadingExercises: boolean;
    isLoadingExercise: boolean;
    isLoadingPhonemeDetails: boolean;
    isLoadingRecommendations: boolean;
    isRatingRecommendation: boolean;
    isLoadingAlerts: boolean;
    isCreatingAlert: boolean;
    isDismissingAlert: boolean;
    isLoadingPhonemeTimings: boolean;
    isGeneratingPhonemeAudio: boolean;
    isLoadingWaveform: boolean;
    isLoadingFeedback: boolean;
    isUpdatingFeedbackTone: boolean;
    isComparingAudio: boolean;
    isLoadingAvatarDemo: boolean;

    // Error States
    exercisesError: string | null;
    exerciseError: string | null;
    phonemeDetailsError: string | null;
    recommendationsError: string | null;
    ratingError: string | null;
    alertsError: string | null;
    createAlertError: string | null;
    dismissAlertError: string | null;
    phonemeTimingsError: string | null;
    generatePhonemeAudioError: string | null;
    waveformError: string | null;
    feedbackError: string | null;
    updateFeedbackToneError: string | null;
    audioComparisonError: string | null;
    avatarDemoError: string | null;
}

const initialState: SpeakingState = {
    exercises: [],
    currentExercise: null,
    phonemeDetails: [],
    recommendedExercises: [],

    mispronunciationAlerts: null,
    phonemeTimings: [],
    generatedPhonemeAudio: null,
    waveformData: null,
    conversationalFeedback: null,
    currentFeedbackTone: 'encouraging',
    audioComparison: null,
    avatarDemonstration: null,

    totalExercises: 0,
    currentPage: START_PAGE,
    hasNextPage: false,
    hasPreviousPage: false,

    filters: {},

    isLoadingExercises: false,
    isLoadingExercise: false,
    isLoadingPhonemeDetails: false,
    isLoadingRecommendations: false,
    isRatingRecommendation: false,
    isLoadingAlerts: false,
    isCreatingAlert: false,
    isDismissingAlert: false,
    isLoadingPhonemeTimings: false,
    isGeneratingPhonemeAudio: false,
    isLoadingWaveform: false,
    isLoadingFeedback: false,
    isUpdatingFeedbackTone: false,
    isComparingAudio: false,
    isLoadingAvatarDemo: false,

    exercisesError: null,
    exerciseError: null,
    phonemeDetailsError: null,
    recommendationsError: null,
    ratingError: null,
    alertsError: null,
    createAlertError: null,
    dismissAlertError: null,
    phonemeTimingsError: null,
    generatePhonemeAudioError: null,
    waveformError: null,
    feedbackError: null,
    updateFeedbackToneError: null,
    audioComparisonError: null,
    avatarDemoError: null,
};

const speakingSlice = createSlice({
    name: 'speaking',
    initialState,
    reducers: {
        setCurrentPage: (state, action) => {
            state.currentPage = action.payload;
        },
        setFilters: (state, action) => {
            state.filters = action.payload;
        },
        clearState: (state) => {
            state.currentExercise = initialState.currentExercise
            state.phonemeDetails = initialState.phonemeDetails
            state.recommendedExercises = initialState.recommendedExercises
            state.mispronunciationAlerts = initialState.mispronunciationAlerts
            state.phonemeTimings = initialState.phonemeTimings
            state.generatedPhonemeAudio = initialState.generatedPhonemeAudio
            state.waveformData = initialState.waveformData
            state.conversationalFeedback = initialState.conversationalFeedback
            state.currentFeedbackTone = initialState.currentFeedbackTone
            state.audioComparison = initialState.audioComparison
            state.avatarDemonstration = initialState.avatarDemonstration
            state.totalExercises = initialState.totalExercises
            state.currentPage = initialState.currentPage
            state.hasNextPage = initialState.hasNextPage
            state.hasPreviousPage = initialState.hasPreviousPage
            state.filters = initialState.filters
            state.isLoadingExercises = initialState.isLoadingExercises
            state.isLoadingExercise = initialState.isLoadingExercise
            state.isLoadingPhonemeDetails = initialState.isLoadingPhonemeDetails
            state.isLoadingRecommendations = initialState.isLoadingRecommendations
            state.isRatingRecommendation = initialState.isRatingRecommendation
            state.isLoadingAlerts = initialState.isLoadingAlerts
            state.isCreatingAlert = initialState.isCreatingAlert
            state.isDismissingAlert = initialState.isDismissingAlert
            state.isLoadingPhonemeTimings = initialState.isLoadingPhonemeTimings
            state.isGeneratingPhonemeAudio = initialState.isGeneratingPhonemeAudio
            state.isLoadingWaveform = initialState.isLoadingWaveform
            state.isLoadingFeedback = initialState.isLoadingFeedback
            state.isUpdatingFeedbackTone = initialState.isUpdatingFeedbackTone
            state.isComparingAudio = initialState.isComparingAudio
            state.isLoadingAvatarDemo = initialState.isLoadingAvatarDemo
            state.exercisesError = initialState.exercisesError
            state.exerciseError = initialState.exerciseError
            state.phonemeDetailsError = initialState.phonemeDetailsError
            state.recommendationsError = initialState.recommendationsError
            state.ratingError = initialState.ratingError
            state.alertsError = initialState.alertsError
            state.createAlertError = initialState.createAlertError
            state.dismissAlertError = initialState.dismissAlertError
            state.phonemeTimingsError = initialState.phonemeTimingsError
            state.generatePhonemeAudioError = initialState.generatePhonemeAudioError
            state.waveformError = initialState.waveformError
            state.feedbackError = initialState.feedbackError
            state.updateFeedbackToneError = initialState.updateFeedbackToneError
            state.audioComparisonError = initialState.audioComparisonError
            state.avatarDemoError = initialState.avatarDemoError
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(globalReset, () => initialState)

            // === FETCH EXERCISES ===
            .addCase(fetchExercisesRequest, (state, action) => {
                state.isLoadingExercises = true;
                state.exercisesError = null;
            })
            .addCase(fetchExercisesSuccess, (state, action) => {
                state.isLoadingExercises = false;
                state.exercises = action.payload.results;
                state.totalExercises = action.payload.count;
                state.hasNextPage = !!action.payload.next;
                state.hasPreviousPage = !!action.payload.previous;
            })
            .addCase(fetchExercisesFailure, (state, action) => {
                state.isLoadingExercises = false;
                state.exercisesError = action.payload;
            })

            // === FETCH EXERCISE BY ID ===
            .addCase(fetchExerciseByIdRequest, (state) => {
                state.isLoadingExercise = true;
                state.exerciseError = null;
            })
            .addCase(fetchExerciseByIdSuccess, (state, action) => {
                state.isLoadingExercise = false;
                state.currentExercise = action.payload;
            })
            .addCase(fetchExerciseByIdFailure, (state, action) => {
                state.isLoadingExercise = false;
                state.exerciseError = action.payload;
            })

            // === FETCH PHONEME DETAILS ===
            .addCase(fetchPhonemeDetailsRequest, (state) => {
                state.isLoadingPhonemeDetails = true;
                state.phonemeDetailsError = null;
            })
            .addCase(fetchPhonemeDetailsSuccess, (state, action) => {
                state.isLoadingPhonemeDetails = false;
                state.phonemeDetails = action.payload;
            })
            .addCase(fetchPhonemeDetailsFailure, (state, action) => {
                state.isLoadingPhonemeDetails = false;
                state.phonemeDetailsError = action.payload;
            })

            // === FETCH RECOMMENDED EXERCISES ===
            .addCase(fetchRecommendedExercisesRequest, (state) => {
                state.isLoadingRecommendations = true;
                state.recommendationsError = null;
            })
            .addCase(fetchRecommendedExercisesSuccess, (state, action) => {
                state.isLoadingRecommendations = false;
                state.recommendedExercises = action.payload;
            })
            .addCase(fetchRecommendedExercisesFailure, (state, action) => {
                state.isLoadingRecommendations = false;
                state.recommendationsError = action.payload;
            })

            // === RATE RECOMMENDATION ===
            .addCase(rateRecommendationRequest, (state) => {
                state.isRatingRecommendation = true;
                state.ratingError = null;
            })
            .addCase(rateRecommendationSuccess, (state) => {
                state.isRatingRecommendation = false;
            })
            .addCase(rateRecommendationFailure, (state, action) => {
                state.isRatingRecommendation = false;
                state.ratingError = action.payload;
            })

            // === PRONUNCIATION ALERTS ===
            .addCase(fetchMispronunciationAlertsRequest, (state) => {
                state.isLoadingAlerts = true;
                state.alertsError = null;
            })
            .addCase(fetchMispronunciationAlertsSuccess, (state, action) => {
                state.isLoadingAlerts = false;
                state.mispronunciationAlerts = action.payload;
            })
            .addCase(fetchMispronunciationAlertsFailure, (state, action) => {
                state.isLoadingAlerts = false;
                state.alertsError = action.payload;
            })

            .addCase(createPronunciationAlertRequest, (state) => {
                state.isCreatingAlert = true;
                state.createAlertError = null;
            })
            .addCase(createPronunciationAlertSuccess, (state) => {
                state.isCreatingAlert = false;
            })
            .addCase(createPronunciationAlertFailure, (state, action) => {
                state.isCreatingAlert = false;
                state.createAlertError = action.payload;
            })

            .addCase(dismissPronunciationAlertRequest, (state) => {
                state.isDismissingAlert = true;
                state.dismissAlertError = null;
            })
            .addCase(dismissPronunciationAlertSuccess, (state) => {
                state.isDismissingAlert = false;
                // Remove dismissed alert from state
                if (state.mispronunciationAlerts) {
                    state.mispronunciationAlerts.should_notify = false;
                }
            })
            .addCase(dismissPronunciationAlertFailure, (state, action) => {
                state.isDismissingAlert = false;
                state.dismissAlertError = action.payload;
            })

            // === PHONEME TIMINGS ===
            .addCase(fetchPhonemeTimingsRequest, (state) => {
                state.isLoadingPhonemeTimings = true;
                state.phonemeTimingsError = null;
            })
            .addCase(fetchPhonemeTimingsSuccess, (state, action) => {
                state.isLoadingPhonemeTimings = false;
                state.phonemeTimings = action.payload;
            })
            .addCase(fetchPhonemeTimingsFailure, (state, action) => {
                state.isLoadingPhonemeTimings = false;
                state.phonemeTimingsError = action.payload;
            })

            // === GENERATE PHONEME AUDIO ===
            .addCase(generatePhonemeAudioRequest, (state) => {
                state.isGeneratingPhonemeAudio = true;
                state.generatePhonemeAudioError = null;
            })
            .addCase(generatePhonemeAudioSuccess, (state, action) => {
                state.isGeneratingPhonemeAudio = false;
                state.generatedPhonemeAudio = action.payload;
            })
            .addCase(generatePhonemeAudioFailure, (state, action) => {
                state.isGeneratingPhonemeAudio = false;
                state.generatePhonemeAudioError = action.payload;
            })

            // === WAVEFORM DATA ===
            .addCase(fetchWaveformDataRequest, (state) => {
                state.isLoadingWaveform = true;
                state.waveformError = null;
            })
            .addCase(fetchWaveformDataSuccess, (state, action) => {
                state.isLoadingWaveform = false;
                state.waveformData = action.payload;
            })
            .addCase(fetchWaveformDataFailure, (state, action) => {
                state.isLoadingWaveform = false;
                state.waveformError = action.payload;
            })

            // === CONVERSATIONAL FEEDBACK ===
            .addCase(fetchConversationalFeedbackRequest, (state) => {
                state.isLoadingFeedback = true;
                state.feedbackError = null;
            })
            .addCase(fetchConversationalFeedbackSuccess, (state, action) => {
                state.isLoadingFeedback = false;
                state.conversationalFeedback = action.payload;
            })
            .addCase(fetchConversationalFeedbackFailure, (state, action) => {
                state.isLoadingFeedback = false;
                state.feedbackError = action.payload;
            })

            // === UPDATE FEEDBACK TONE ===
            .addCase(updateFeedbackToneRequest, (state) => {
                state.isUpdatingFeedbackTone = true;
                state.updateFeedbackToneError = null;
            })
            .addCase(updateFeedbackToneSuccess, (state) => {
                state.isUpdatingFeedbackTone = false;
            })
            .addCase(updateFeedbackToneFailure, (state, action) => {
                state.isUpdatingFeedbackTone = false;
                state.updateFeedbackToneError = action.payload;
            })

            // === AUDIO COMPARISON ===
            .addCase(compareAudioSimilarityRequest, (state) => {
                state.isComparingAudio = true;
                state.audioComparisonError = null;
            })
            .addCase(compareAudioSimilaritySuccess, (state, action) => {
                state.isComparingAudio = false;
                state.audioComparison = action.payload;
            })
            .addCase(compareAudioSimilarityFailure, (state, action) => {
                state.isComparingAudio = false;
                state.audioComparisonError = action.payload;
            })

            // === AVATAR DEMONSTRATION ===
            .addCase(fetchAvatarDemonstrationRequest, (state) => {
                state.isLoadingAvatarDemo = true;
                state.avatarDemoError = null;
            })
            .addCase(fetchAvatarDemonstrationSuccess, (state, action) => {
                state.isLoadingAvatarDemo = false;
                state.avatarDemonstration = action.payload;
            })
            .addCase(fetchAvatarDemonstrationFailure, (state, action) => {
                state.isLoadingAvatarDemo = false;
                state.avatarDemoError = action.payload;
            })

            // === FILTERS ===
            .addCase(setExerciseFilters, (state, action) => {
                state.filters = action.payload;
                state.currentPage = 1; // Reset to first page when filters change
            })
            .addCase(clearExerciseFilters, (state) => {
                state.filters = {};
                state.currentPage = 1;
            })

            // === FEEDBACK TONE ===
            .addCase(setCurrentFeedbackTone, (state, action) => {
                state.currentFeedbackTone = action.payload;
            });
    },
});

export const { setCurrentPage, setFilters, clearState } = speakingSlice.actions;

export default speakingSlice.reducer;
